import { useMemo } from 'react';

import { EntityKind, EntityStage, EntityWhereInput } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { useGetEntityOptionsQuery } from './graphql/GetEntityOptions.generated';

interface EntityOption {
  display: string;
  kind: EntityKind;
  stage: EntityStage;
  value: string;
}

/**
 * Hook to get entity options for a given household ID with optional filtering
 *
 * @param householdId - ID of the household to get entities for
 * @param entitiesLike - Optional filter criteria for entities
 * @returns Array of entity options with display, kind, and value properties
 */
export function useGetEntityOptions(
  householdId: string,
  entitiesLike: EntityWhereInput = {}
) {
  const { data, loading, error } = useGetEntityOptionsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      entitiesLike: {
        hasHouseholdWith: [
          {
            id: householdId,
          },
        ],
        ...entitiesLike,
      },
    },
    skip: !householdId,
  });

  // Transform the entities into select options
  const entityOptions: EntityOption[] = useMemo(() => {
    const entities = getNodes(data?.entities);

    return entities.map((entity) => ({
      display: entity.subtype.displayName,
      kind: entity.kind,
      stage: entity.stage,
      value: entity.id,
    }));
  }, [data]);

  return {
    entityOptions,
    loading,
    error,
  };
}
